import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { loginUser, clearSuccess } from "../../store/slices/authSlice";

import login_img from "../../assets/images/login_img.png";
import login_logo from "../../assets/images/login_logo.png";
import eye_icon from "../../assets/images/eye_icon.svg";
import eye_slash_icon from "../../assets/images/eye_slash_icon.svg";
import { encryptPassword } from "../../utils/cryptoUtils";

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading, error, success } = useAppSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checked, setChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (loading) return;

    try {
      const encryptedPassword = await encryptPassword(password);

      await dispatch(
        loginUser({
          email: email.trim(),
          password: encryptedPassword,
          rememberMe: checked,
        }),
      ).unwrap();
      // Login successful.
      // Authentication cookies are already set by the backend.
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate("/forgotpassword");
  };

  return (
    <div className="login py_14">
      <div className="container-fluid h-100">
        <div className="row h-100">
          {/* LEFT SIDE */}
          <div className="col-md-6 h-100">
            <div className="login_image">
              <div className="content_set">
                <div className="login_text_overlay">
                  <h2>
                    Smart Accounting <br />
                    Starts Here
                  </h2>

                  <p>
                    Manage users, bank accounts, and payroll — securely and
                    efficiently with UnivisionCPA.
                  </p>
                </div>

                <div className="login_content"></div>
                <div className="login_total"></div>
              </div>

              <img src={login_img} alt="Login Background" />
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="col-md-6">
            <div className="login_form h-100 d-flex flex-column align-item-center justify-content-center px_50">
              <img
                src={login_logo}
                className="w-auto mw-auto mb_60"
                alt="UnivisionCPA"
              />

              <h4 className="mb_4">Welcome Back!</h4>

              <p>Please provide your credentials to continue.</p>

              <form
                className="common_input_forms mt_0"
                onSubmit={submitHandler}
              >
                {/* EMAIL */}
                <div className="form_group">
                  <label htmlFor="email">Email Address</label>

                  <input
                    id="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter Email Address"
                    value={email}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* PASSWORD */}
                <div className="form_group password_field">
                  <label htmlFor="password">Password</label>

                  <div className="password_input_wrapper">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="form-control password_input"
                      placeholder="Enter Password"
                      value={password}
                      autoComplete="current-password"
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />

                    <button
                      type="button"
                      className="password_eye_button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <img
                        src={showPassword ? eye_slash_icon : eye_icon}
                        alt=""
                      />
                    </button>
                  </div>
                </div>

                {/* REMEMBER + FORGOT */}
                <div className="d-flex mt_30">
                  <div className="w-50">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="rememberMe"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                      />

                      <label className="form-check-label" htmlFor="rememberMe">
                        Remember for 30 days
                      </label>
                    </div>
                  </div>

                  <div className="w-50 text-end">
                    <button
                      type="button"
                      className="btn_link border-0 bg-transparent p-0"
                      onClick={handleForgotPassword}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* ERROR */}
                {error && <p className="text-danger mt_20 mb-0">{error}</p>}

                {/* SUCCESS */}
                {success && <div className="text-success mt_20">{success}</div>}

                {/* LOGIN BUTTON */}
                <div className="d-flex w-100 mt_30">
                  <button
                    type="submit"
                    className="btn btn_primary px_30 ms-auto"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Logging in
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
